/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Callout } from '@studio/design-system/callout';
import { Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import { Label } from '@studio/design-system/label';
import * as Switch from '@studio/design-system/switch';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { ActionBox, Heading } from '../layout/index.js';
import { useWiring } from '../wiring.js';
import type { Props } from './types.js';
import { useVerifiedAction } from '#~/auth/email-verification-gate/use-verified-action.jsx';
import { useDepot } from '#~/depot/hooks.js';

/** `<Password>` panel. */
export const Password = (props: Props): ReactElement => {
  const wiring = useWiring();
  const user = useDepot((µ) => µ.user)!;
  // Password reset needs a verified email; the prompt holds the user here and continues on verify.
  const verifiedAction = useVerifiedAction('password_reset');

  const handleChangePassword = useLatestFn((e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    void verifiedAction.run(() => wiring.handleChangePassword(user));
  });

  const handleEnrollMFA = useLatestFn((): void => {
    wiring.handleEnrollMFA(user);
  });

  const handleUnenrollMFA = useLatestFn((): void => {
    wiring.handleUnenrollMFA(user);
  });

  // ENG-3451: a Google-federated account has no Studio-side password or MFA
  // to manage — those credentials live at Google, and Auth0 rejects
  // password/MFA management calls for them. Render concise explanatory
  // content INSTEAD of the controls. Only the verified 'google' value takes
  // this branch: empty/unknown provider preserves the existing controls
  // untouched.
  if (user.provider === 'google') {
    return (
      <div {...props}>
        <ActionBox className='mb-32'>
          <Heading subtitle='Update your password and security settings.' title='Security' />
        </ActionBox>
        <Callout
          data-stag='account.settings.security.google_provider'
          title='You sign in with Google'
        >
          <Callout.P>
            Your {vendorName()} account uses Google sign-in, so your password and two-factor
            authentication are managed by your Google account. There&rsquo;s nothing to change here.
          </Callout.P>
        </Callout>
      </div>
    );
  }

  return (
    <div {...props}>
      <ActionBox className='mb-32'>
        <Heading subtitle='Update your password and security settings.' title='Security' />
      </ActionBox>
      <div className='my-16 flex w-[50%] flex-col gap-32'>
        <Group
          label={
            <div className='flex'>
              <Label>Password</Label>
              <div className='flex-1' />
              <a className='text-sm' href='#' onClick={handleChangePassword}>
                Change
              </a>
            </div>
          }
        >
          <Input type='password' value={user.id} />
        </Group>

        <Switch.Container className='h-34' label='2FA Enabled'>
          <Switch.Archetype
            checked={!!user.mfaEnabled}
            onCheckedChange={user.mfaEnabled ? handleUnenrollMFA : handleEnrollMFA}
          />
        </Switch.Container>
      </div>
      {verifiedAction.prompt}
    </div>
  );
};
